import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { deleteDull, findAllDull } from "./services/dull.services";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  clearFilterDull,
  clearDullDetail,
  setFilterDull,
  setDullID,
} from "./dullSlice";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { LoadingPanel } from "../../components/layout/Loading";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import { FaEye } from "react-icons/fa";

const Dull: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  useEffect(() => {
    return () => {
      dispatch(closeDialog());
      dispatch(clearDullDetail());
      dispatch(setCurrentPage(0));
    };
  }, []);

  const handleProcessCreate = () => {
    navigate("/dull/create");
  };
  return (
    <>
      <DeleteDullDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Dull Issue List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleProcessCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Add New
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <DullGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const DullGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const DullList = useAppSelector((state) => state.dull.DullList);
  const loading = useAppSelector((state) => state.dull.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const FilterDull = useAppSelector((state) => state.dull.FilterDull);
  useEffect(() => {
    const payload = {
      ...FilterDull,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(findAllDull(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const MyEditCommandCell = (props: any) => {
    const handleEditItem = (dull_issue_guid: string) => {
      navigate("/dull/edit", {
        state: { dull_issue_guid: dull_issue_guid },
      });
    };

    const handleOpenDeleteDialog = (dull_issue_guid: string) => {
      dispatch(openDialog("deleteDull"));
      dispatch(setDullID(dull_issue_guid));
    };
    const handlePrint = (dull_issue_guid: string) => {
      window.open(`/dullissue/${dull_issue_guid}`, "_blank");
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            themeColor="success"
            onClick={() => handleEditItem(props.dataItem.dull_issue_guid)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem.dull_issue_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}

        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="View"
            themeColor="tertiary"
            onClick={() => handlePrint(props.dataItem.dull_issue_guid)}
          >
            <FaEye className="absolute-position" style={{ fontSize: "24px" }} />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 5 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const DullFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.dull.FilterDull}
        setFilterAction={setFilterDull}
        clearFilterAction={clearFilterDull}
        fetchDataAction={findAllDull}
        pagination={{ case: "lowercase" }}
      />
    ),
    []
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        scrollable="scrollable"
        className="responsive-table"
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        filter={filter}
        data={filter ? filterBy(DullList, filter) : DullList}
        total={DullList[0]?.itemcount || DullList.length}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          width={100}
          field="dull_issue_no"
          title="Dull No"
          cells={{
            data: (props: any) => (
              <td>
                <div
                  className="hoverableName"
                  onClick={() =>
                    navigate("/dull/edit", {
                      state: {
                        dull_issue_guid: props.dataItem?.dull_issue_guid,
                      },
                    })
                  }
                >
                  {props.dataItem?.dull_issue_no}
                </div>
              </td>
            ),
          }}
        />
        <Column
          field="issue_date"
          title="Issue Date"
          width={120}
          cells={{ filterCell: DullFilterCell }}
        />
        <Column
          field="karigar_name"
          title="Karigar Name"
          width={200}
          cells={{ filterCell: DullFilterCell }}
        />
        <Column
          field="approx_delivery_date"
          title="Approx Delivery Date"
          width={180}
          cells={{ filterCell: DullFilterCell }}
        />
        {/* <Column field="remarks" title="Remarks" width={180} /> */}

        {(isEditAccess || isDeleteAccess) && (
          <Column
            field="dull_issue_guid"
            title="Actions"
            cells={{ data: MyEditCommandCell }}
            width={"110px"}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeleteDullDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const DullID = useAppSelector((state) => state.dull.DullID);
  const FilterDull = useAppSelector((state) => state.dull.FilterDull);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  const handleDeleteAction = async (ID: number | null) => {
    const getpayload = {
      ...FilterDull,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    if (ID) {
      const response = await dispatch(deleteDull(ID));
      dispatch(setCurrentPage(0));
      if (response.payload?.Data.Status === 200) {
        dispatch(findAllDull(getpayload));
        dispatch(closeDialog());
        dispatch(clearDullDetail());
      } else {
        dispatch(findAllDull(getpayload));
        dispatch(closeDialog());
        dispatch(clearDullDetail());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteDull" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Dull?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Dull?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    handleDeleteAction(DullID);
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default Dull;
